// Copyright (c) Meta Platforms, Inc. and affiliates.

/**
 * @file text.stylex.ts
 * @input Uses StyleX, theme tokens
 * @output Exports StyleX styles for Text and Heading props
 * @position Styles module; consumed by Text.tsx, Heading.tsx
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Text/Text.doc.mjs
 */

import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, fontWeightVars, textSizeVars, typeScaleVars, typographyVars } from "../theme/tokens.stylex.js";

// =============================================================================
// Color Styles
// =============================================================================

export const colorStyles = {
  primary: {
    kMwMTN: "x1tgivj0",
    $$css: true
  },
  secondary: {
    kMwMTN: "xv1l7n4",
    $$css: true
  },
  disabled: {
    kMwMTN: "xnbbluu",
    $$css: true
  },
  placeholder: {
    kMwMTN: "xv1l7n4",
    $$css: true
  },
  accent: {
    kMwMTN: "xjse4m1",
    $$css: true
  },
  inherit: {
    kMwMTN: "x1heor9g",
    $$css: true
  }
};

// =============================================================================
// Weight Styles
// =============================================================================

export const weightStyles = {
  normal: {
    k63SB2: "x1sodnla",
    $$css: true
  },
  medium: {
    k63SB2: "x1e4wzip",
    $$css: true
  },
  semibold: {
    k63SB2: "x2mo6ok",
    $$css: true
  },
  bold: {
    k63SB2: "x1lvx875",
    $$css: true
  }
};

// =============================================================================
// Default Weight by Type (matches theme type-scale tokens)
// =============================================================================

export const defaultWeightByTypeStyles = {
  body: {
    k63SB2: "xxovm9e",
    $$css: true
  },
  large: {
    k63SB2: "x149oux8",
    $$css: true
  },
  label: {
    k63SB2: "xmhvcl5",
    $$css: true
  },
  code: {
    k63SB2: "xx3eeay",
    $$css: true
  },
  supporting: {
    k63SB2: "xv8on6e",
    $$css: true
  },
  "display-1": {
    k63SB2: "x1txul5o",
    $$css: true
  },
  "display-2": {
    k63SB2: "x1y36c3f",
    $$css: true
  },
  "display-3": {
    k63SB2: "x1on40hk",
    $$css: true
  },
  inherit: {
    k63SB2: "x1pd3egz",
    $$css: true
  }
};

// =============================================================================
// Baseline Size/Leading by Type (from type-scale tokens)
//
// These ensure Text renders with correct sizing even without a theme.
// Theme component overrides (.astryx-text.body { ... } today, plus data-type
// reflection on rendered text elements) win when present
// because they have higher specificity via @scope.
// =============================================================================

export const sizeByTypeStyles = {
  body: {
    kGuDYH: "xjm74w1",
    kLWn49: "xw6l6zx",
    $$css: true
  },
  large: {
    kGuDYH: "x18juvz8",
    kLWn49: "xf74fhv",
    $$css: true
  },
  label: {
    kGuDYH: "xcr08ib",
    kLWn49: "x1kq96og",
    $$css: true
  },
  code: {
    kGuDYH: "xp03k98",
    kLWn49: "x17iicif",
    kMv6JI: "x9m5x89",
    $$css: true
  },
  supporting: {
    kGuDYH: "x141an7d",
    kLWn49: "x1ltkj2j",
    $$css: true
  },
  "display-1": {
    kGuDYH: "xsub3ws",
    kLWn49: "x112ttwr",
    $$css: true
  },
  "display-2": {
    kGuDYH: "x1yego12",
    kLWn49: "xh0iwvy",
    $$css: true
  },
  "display-3": {
    kGuDYH: "xlgnzhf",
    kLWn49: "x1ujwuaq",
    $$css: true
  },
  inherit: {
    kGuDYH: "x1qlqyl8",
    kLWn49: "x15bjb6t",
    $$css: true
  }
};

// =============================================================================
// Explicit Size Override
// =============================================================================

export const sizeStyles = {
  "4xs": {
    kGuDYH: "xxc45ev",
    $$css: true
  },
  "3xs": {
    kGuDYH: "x10p7juq",
    $$css: true
  },
  "2xs": {
    kGuDYH: "x16a80zy",
    $$css: true
  },
  xsm: {
    kGuDYH: "x51wmvv",
    $$css: true
  },
  sm: {
    kGuDYH: "x1eqnyfr",
    $$css: true
  },
  base: {
    kGuDYH: "x1j29vfg",
    $$css: true
  },
  lg: {
    kGuDYH: "xc7cgfe",
    $$css: true
  },
  xl: {
    kGuDYH: "x1wqms48",
    $$css: true
  },
  "2xl": {
    kGuDYH: "xhs0kqb",
    $$css: true
  },
  "3xl": {
    kGuDYH: "x10srzze",
    $$css: true
  },
  "4xl": {
    kGuDYH: "xqcvi3d",
    $$css: true
  }
};

// =============================================================================
// Baseline Size/Leading by Heading Level (from type-scale tokens)
//
// Same rationale as sizeByTypeStyles — ensures Heading renders
// with correct sizing even without a theme.
// =============================================================================

export const sizeByLevelStyles = {
  "1": {
    kGuDYH: "xcg7oai",
    kLWn49: "xfmsba7",
    k63SB2: "x1v68xuy",
    $$css: true
  },
  "2": {
    kGuDYH: "x1xvnhcw",
    kLWn49: "x1cpk1wn",
    k63SB2: "x12yy4cs",
    $$css: true
  },
  "3": {
    kGuDYH: "xii13ha",
    kLWn49: "xwjzt0u",
    k63SB2: "x1jcxfy8",
    $$css: true
  },
  "4": {
    kGuDYH: "x8tkxat",
    kLWn49: "xqerer",
    k63SB2: "x2hcmsi",
    $$css: true
  },
  "5": {
    kGuDYH: "xsgqta0",
    kLWn49: "xo3gurs",
    k63SB2: "xno150v",
    $$css: true
  },
  "6": {
    kGuDYH: "xw5ohdf",
    kLWn49: "xeixjfn",
    k63SB2: "x1pw4frv",
    $$css: true
  }
};

// =============================================================================
// Display Styles
// =============================================================================

export const displayStyles = {
  inline: {
    k1xSpc: "xt0psk2",
    $$css: true
  },
  block: {
    k1xSpc: "x1lliihq",
    $$css: true
  }
};

// =============================================================================
// Truncation Styles
// =============================================================================

export const truncationStyles = {
  singleLine: {
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    khDVqt: "xuxw1ft",
    k1xSpc: "x1lliihq",
    $$css: true
  },
  multiLine: {
    kVQacm: "xb3r6kr",
    k1xSpc: "x104kibb",
    kgKLqz: "x1ua5tub",
    $$css: true
  }
};

// =============================================================================
// Word Break Styles
// =============================================================================

export const wordBreakStyles = {
  "break-word": {
    kTgw9: "x1lldw8n",
    kHjlTd: "x1mzt3pk",
    $$css: true
  },
  "break-all": {
    kTgw9: "x1yn0g08",
    $$css: true
  }
};

// =============================================================================
// Text Wrap Styles
// =============================================================================

export const textWrapStyles = {
  wrap: {
    kN2L0X: "xk4td0m",
    $$css: true
  },
  nowrap: {
    kN2L0X: "xebhuq6",
    $$css: true
  },
  balance: {
    kN2L0X: "x1w2vvpw",
    $$css: true
  },
  pretty: {
    kN2L0X: "x1fzhlzt",
    $$css: true
  }
};

// =============================================================================
// Capsize Styles (Text Box Trim)
// =============================================================================

export const capsizeStyles = {
  enabled: {
    kxwWH2: "x1b2iylo",
    kzeHkT: "xwgcxoh",
    k1xSpc: "x1lliihq",
    $$css: true
  }
};

// =============================================================================
// Decoration Styles
// =============================================================================

export const decorationStyles = {
  strikethrough: {
    kybGjl: "xmqliwb",
    $$css: true
  }
};

// =============================================================================
// Tabular Numbers Style
// =============================================================================

export const tabularNumbersStyle = {
  enabled: {
    kcqcaj: "xss6m8b",
    $$css: true
  }
};

// =============================================================================
// Justify (Text Alignment) Styles
// =============================================================================

export const justifyStyles = {
  start: {
    k9WMMc: "x1yc453h",
    $$css: true
  },
  center: {
    k9WMMc: "x2b8uid",
    $$css: true
  },
  end: {
    k9WMMc: "xp4054r",
    $$css: true
  }
};

// =============================================================================
// Truncation Tooltip Content Style
// =============================================================================

export const truncationTooltipStyles = {
  content: {
    ks0D6T: "xw5ewwj",
    kTgw9: "x13faqbe",
    $$css: true
  }
};